import { FC, useEffect, useMemo, useState } from 'react'
import { Button, message, Modal, Typography } from 'antd'
import {
    FICTIV_TERMS_SERVICES,
    EAR_SUPPORT,
    ITAR_SUPPORT,
} from '@Builder/QuotesHomePage/QuotesDropzone/logic'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import getColor from '~/lib/getColor'
import Link from '~/core/Link'
import { SALES_EMAIL } from '~/lib/emailAndUrls'
import Image from '~/core/Image'
import Box from '~/core/Box'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import {
    useItarAcceptedDatetimeQuery,
    useUpdateItarAcceptedDatetimeMutation,
} from '~/gql/graphql'
import { getUserIdentity } from '~/redux-modules/identity'
import {
    QUOTES_PATH,
    EXPORT_CONTORL_QUERY_STRING,
} from '~/redux-modules/preferences'
import logger from '~/lib/logger'

import styles from './itarModalStyles.css'
import itarMapImg from './itar-map.png'
import ExportControlModal from './ExportControlModal'

const ItarModal: FC = () => {
    const identity = useSelector(getUserIdentity)
    const params = useParams()
    const navigate = useNavigate()
    const displayExportControlSelfCertification = useFlagEnabled(
        PROVISIONED_UI_FLAGS.DISPLAY_EXPORT_CONTROL_SELF_CERTIFICATION,
    )

    const {
        userId: oboUserId,
        isRestrictedPage,
        isQuoteDetailPage,
    } = useMemo(() => getOboRestrictions(params), [params])

    const userId: number = oboUserId || identity?.id
    const [isModalVisible, setIsModalVisible] = useState(false)
    // To prevent modal from popping back up
    const [userAcceptedTerms, setUserAcceptedTerms] = useState(false)
    const [isExportControlModalVisible, setIsExportControlModalVisible] =
        useState(false)

    const { data: itarResult, loading: itarLoading } =
        useItarAcceptedDatetimeQuery({
            variables: { userId },
        })
    const [updateItarAcceptedDatetime] = useUpdateItarAcceptedDatetimeMutation()

    const handleAcceptItarTerms = async () => {
        try {
            await updateItarAcceptedDatetime({
                variables: { userId },
            })

            setUserAcceptedTerms(true)
            if (!isQuoteDetailPage) {
                navigate(`${QUOTES_PATH}/upload?${EXPORT_CONTORL_QUERY_STRING}`)
            }
        } catch (err) {
            logger.warn(`Failed to update ITAR acceptance for user ${userId}`)
            message.error(
                'We were unable to update your acceptance of our terms. Please try again.',
            )
        }
    }

    useEffect(() => {
        // Display regular modal
        if (
            displayExportControlSelfCertification &&
            !oboUserId &&
            !isRestrictedPage
        ) {
            if (itarResult?.user?.itarAcceptedDatetime) {
                setIsModalVisible(false)
            } else if (itarResult?.user?.itarAcceptedDatetime === null) {
                setIsModalVisible(true)
            }
        }

        // Display OBO export control modal
        if (
            displayExportControlSelfCertification &&
            oboUserId &&
            oboUserId !== identity?.id &&
            isRestrictedPage &&
            itarResult?.user?.itarAcceptedDatetime === null
        ) {
            setIsExportControlModalVisible(true)
        }
    }, [
        displayExportControlSelfCertification,
        itarResult?.user?.itarAcceptedDatetime,
        identity?.id,
        isRestrictedPage,
        oboUserId,
        setIsModalVisible,
        setIsExportControlModalVisible,
    ])

    if (isExportControlModalVisible) {
        return (
            <ExportControlModal
                visible={isExportControlModalVisible}
                setVisible={setIsExportControlModalVisible}
                userId={userId}
                userEmail={itarResult?.user?.email}
            />
        )
    }

    return (
        <Modal
            title={
                <Typography.Text
                    style={{
                        fontSize: '16px',
                        color: getColor('blackAlpha65'),
                        fontWeight: 400,
                    }}
                >
                    Welcome to Fictiv! A quick reminder...
                </Typography.Text>
            }
            destroyOnClose={true}
            className={styles.itarModal}
            centered
            maskClosable={false}
            visible={isModalVisible && !userAcceptedTerms}
            closable={false}
            width="550px"
            footer={[
                <Button
                    block
                    name="agree-and-upload-files"
                    key="submit"
                    type="primary"
                    onClick={handleAcceptItarTerms}
                    data-test-target="agree-and-upload-files"
                    style={{
                        padding: '0px 8px',
                    }}
                    size="large"
                    disabled={itarLoading}
                >
                    Continue
                </Button>,
            ]}
        >
            <Box justifyContent="center">
                <Image
                    src={itarMapImg}
                    width="308"
                    style={{
                        marginBottom: '8px',
                    }}
                />
            </Box>
            <Typography.Paragraph className={styles.itarText}>
                As part of Fictiv's commitment to compliance and international
                regulations, we kindly ask you to review and agree to our ITAR
                terms. These terms ensure that our products are used responsibly
                and in accordance with applicable laws and restrictions.
            </Typography.Paragraph>
            <Typography.Paragraph className={styles.itarText}>
                Clicking "Continue" certifies that you understand and agree that{' '}
                <Link
                    href={FICTIV_TERMS_SERVICES}
                    target="_blank"
                    name="fictiv-terms-service"
                    placement="fictiv-terms-services"
                >
                    Fictiv's Terms of Service
                </Link>{' '}
                prohibit uploading data controlled under the{' '}
                <span style={{ fontWeight: 400 }}>
                    Export Administration Regulations
                </span>{' '}
                (
                <Link
                    href={EAR_SUPPORT}
                    target="_blank"
                    name="fictiv-ear-support"
                    placement="ear-support"
                >
                    EAR
                </Link>
                ) or the{' '}
                <span style={{ fontWeight: 400 }}>
                    International Traffic in Arms Regulations
                </span>{' '}
                (
                <Link
                    href={ITAR_SUPPORT}
                    target="_blank"
                    name="fictiv-itar-support"
                    placement="itar-support"
                >
                    ITAR
                </Link>
                ). Furthermore, you certify that the parts produced by Fictiv
                shall not be shipped to an embargoed or sanctioned country,
                prohibited end-user, or used for a prohibited end use.
            </Typography.Paragraph>
            <Typography.Paragraph className={styles.itarText}>
                For any questions, please contact{' '}
                <Link
                    name="contact-sales"
                    href={`mailto:${SALES_EMAIL}?subject=Fictiv - Help With Export Controlled Designs`}
                    target="_blank"
                    placement="contact-sales-department"
                    data-test-target="contact-sales"
                >
                    sales@fictiv.com
                </Link>
            </Typography.Paragraph>
        </Modal>
    )
}

export default ItarModal

function getOboRestrictions(params: { '*'?: string; userId?: string }): {
    userId: number | null
    isRestrictedPage: boolean
    isQuoteDetailPage: boolean
} {
    const wildcard = params['*'] || ''
    const parts = wildcard.split('/')

    if (!wildcard.startsWith('admin/app/user')) {
        return {
            userId: null,
            isRestrictedPage: false,
            isQuoteDetailPage:
                parts[1] === 'quotes' &&
                (parts[2] || '').split('-').length === 5,
        }
    }

    const isTeamsPage = parts[4] === 'team'
    const quoteIndex = isTeamsPage ? 6 : 4
    const isQuotesPage = parts[quoteIndex] === 'quotes'
    const isQuoteDetailPage =
        isQuotesPage && (parts[quoteIndex + 1] || '').split('-').length === 5 // is it a uuid?

    const userId =
        (params.userId ? parseInt(params.userId) : null) ||
        parseInt(parts[3]) ||
        null

    const response = {
        userId,
        isRestrictedPage: isQuotesPage && !!parts[quoteIndex + 1],
        isQuoteDetailPage,
    }

    return response
}
